import { ATTEMPT_MS, MAX_ATTEMPTS, type Attempt } from '@hmawiw/shared';
import { create } from 'zustand';
import { haptic, isNative } from '../native/platform';
import { useCards } from './cards';
import { useEvents } from './events';
import { useSettings } from './settings';

const KEY = 'hmawiw.timer';

export interface RunningTimer {
  cardId: string;
  startedAt: number;
  endsAt: number;
}

interface Persisted {
  running: RunningTimer | null;
  askFor: string | null;
}

interface TimerState extends Persisted {
  /** Logs the attempt on the card at once and starts the ten minutes; replaces any other running timer. */
  start(cardId: string): Promise<void>;
  /** Stops early; the attempt stays logged as not done. */
  stop(): void;
  /** The ten minutes ran out: the attempt becomes done and the card asks how it feels now. */
  finish(): Promise<void>;
  /** The question was answered or dismissed. */
  answer(): void;
  /** Runs finish when the end has passed, else arms the in-page alarm. Called on load, on start and on resume. */
  ensure(): void;
}

function read(): Persisted {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw) as Partial<Persisted>;
      const running = v.running && typeof v.running.cardId === 'string' && typeof v.running.endsAt === 'number' ? v.running : null;
      return { running, askFor: typeof v.askFor === 'string' ? v.askFor : null };
    }
  } catch {
    // no storage
  }
  return { running: null, askFor: null };
}

function write(p: Persisted): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // no storage
  }
}

let alarm: ReturnType<typeof setTimeout> | null = null;

/**
 * On the web the tab itself rings when it is visible; a hidden tab gets a notification, kept on
 * screen when the preference says alarm. The phone has its own alarm scheduled natively.
 */
async function notifyWeb(title: string): Promise<void> {
  if (isNative) return;
  const ring = useSettings.getState().timerRing;
  const visible = typeof document !== 'undefined' && document.visibilityState === 'visible';
  if (visible) {
    if (ring) void playAlarm();
    return;
  }
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker?.ready;
    await reg?.showNotification(title, { tag: 'hmawiw-timer', ...(ring ? { requireInteraction: true, vibrate: [400, 200, 400, 200, 400] } : {}) });
  } catch {
    // no service worker
  }
}

let ringing: HTMLAudioElement | null = null;

/** The alarm sound in the page; allowed because starting the timer was a user gesture. */
export async function playAlarm(): Promise<void> {
  if (typeof Audio === 'undefined') return;
  try {
    stopAlarm();
    ringing = new Audio('/alarm.wav');
    await ringing.play();
  } catch {
    // autoplay refused
  }
}

export function stopAlarm(): void {
  if (!ringing) return;
  ringing.pause();
  ringing = null;
}

export const useTimer = create<TimerState>((set, get) => ({
  ...read(),

  async start(cardId) {
    const card = useCards.getState().cards[cardId];
    if (!card) return;
    const now = Date.now();
    const attempt: Attempt = { at: now, done: false };
    const attempts = [...card.attempts, attempt].slice(-MAX_ATTEMPTS);
    await useCards.getState().updateCard(cardId, { attempts });
    const running = { cardId, startedAt: now, endsAt: now + ATTEMPT_MS };
    set({ running, askFor: null });
    write({ running, askFor: null });
    haptic('confirm');
    get().ensure();
  },

  stop() {
    const { running } = get();
    if (alarm) clearTimeout(alarm);
    alarm = null;
    set({ running: null });
    write({ running: null, askFor: get().askFor });
    if (running) void useEvents.getState().log('attempt', running.cardId, { result: 'stopped' });
  },

  async finish() {
    const { running } = get();
    if (!running) return;
    if (alarm) clearTimeout(alarm);
    alarm = null;
    set({ running: null, askFor: running.cardId });
    write({ running: null, askFor: running.cardId });
    haptic('confirm');
    const card = useCards.getState().cards[running.cardId];
    if (!card) {
      set({ askFor: null });
      write({ running: null, askFor: null });
      return;
    }
    const attempts = card.attempts.map((a) => (a.at === running.startedAt ? { ...a, done: true } : a));
    await useCards.getState().updateCard(card.id, { attempts });
    await useEvents.getState().log('attempt', card.id, { result: 'ran' });
    void notifyWeb(card.title);
  },

  answer() {
    stopAlarm();
    set({ askFor: null });
    write({ running: get().running, askFor: null });
  },

  ensure() {
    const { running } = get();
    if (alarm) clearTimeout(alarm);
    alarm = null;
    if (!running) return;
    const left = running.endsAt - Date.now();
    if (left <= 0) {
      void get().finish();
      return;
    }
    alarm = setTimeout(() => void get().finish(), left);
  },
}));

/** m:ss left on the running timer, never below zero. */
export function timeLeft(running: RunningTimer, now: number): string {
  const s = Math.max(0, Math.ceil((running.endsAt - now) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') useTimer.getState().ensure();
  });
}
